// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@guestsupply/App/Components/Notify/Notify";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@guestsupply/Overrides/Components/OrderSummaryCard";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { Link } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    // XNG-Changes
    title = "title",
    sectionTitleUrl = "sectionTitleUrl",
    sectionTitleText = "sectionTitleText",
    showPONumber = "showPONumber",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.title]: string;
        [fields.sectionTitleUrl]: LinkFieldValue;
        [fields.sectionTitleText]: string;
        [fields.showPONumber]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, {
            ...recentOrdersParameter,
        }),
        settingsCollection: getSettingsCollection(state),
        // XNG-Changes
        canViewOrders: isAuthenticated,
        widgetPageUrl: getPageLinkByNodeId(state, ownProps.fields.sectionTitleUrl.value),
        isAccountPage: getCurrentPage(state).type === "MyAccountPage",
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    // XNG-Change
    titleSectionWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    pagination?: TypographyPresentationProps;
    bold?: TypographyPresentationProps;
    printButton?: LinkPresentationProps;
    link?: LinkPresentationProps;
    icon?: IconPresentationProps;
    dataTableHead?: DataTableHeadProps;
    tableWrapper?: GridItemProps;
    tableStyle?: DataTablePresentationProps;
    wrapper?: InjectableCss;
}

// Xng-Changes: Check RecentOrdersExtensions file for style
export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
};

const defaultCustomCss = ``;

// XNG-Changes: export
export const recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC,erpOrderNumber DESC,webOrderNumber DESC",
    expand: ["orderLines"],
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        // XNG Change:fixing issue not showing the widget when moving from homePge to myaccount and vice versa 
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    render() {
        const { ordersDataView, settingsCollection, canViewOrders, widgetPageUrl, fields, isAccountPage } = this.props;
        const link = widgetPageUrl?.url || undefined;

        if (!canViewOrders || !ordersDataView.value || (!isAccountPage && ordersDataView.value.length === 0)) {
            return null;
        }

        const customCssWrapper = {
            css: css`
                ${styles.wrapper?.css}
                ${this.props.fields.customCSS}
            `,
        };

        // XNG-Change: change structure to match design, add SectionTitle
        return (
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper {...styles.titleSectionWrapper}>
                    <Typography {...styles.title}>{this.props.fields.title}</Typography>

                    {!isAccountPage && (
                        <>
                            <Typography {...styles.pagination}>
                                {translate("Showing")}{" "}
                                <Typography {...styles.bold}>{ordersDataView.pagination?.pageSize}</Typography>{" "}
                                {translate("out of")}{" "}
                                <Typography {...styles.bold}>{ordersDataView.pagination?.totalItemCount}</Typography>
                            </Typography>

                            <Link onClick={openPrintDialog} {...styles.printButton} aria-label="Print">
                                {translate("Print")}
                            </Link>
                        </>
                    )}
                    {link && (
                        <Link
                            href={link}
                            {...styles.link}
                            aria-label={fields.sectionTitleText || translate("View All Orders")}
                        >
                            {fields.sectionTitleText || translate("View More")}
                        </Link>
                    )}
                </StyledWrapper>
                {ordersDataView.value.length === 0 && (
                    <GridItem {...styles.noOrdersFoundGridItem}>
                        <Typography {...styles.noOrdersFoundText}>{translate("No orders found")}</Typography>
                    </GridItem>
                )}

                {ordersDataView.value.length > 0 && (
                    <GridItem {...styles.tableWrapper} width={12}>
                        <DataTable {...styles.tableStyle}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader>{translate("Date")}</DataTableHeader>
                                <DataTableHeader>{translate("Order #")}</DataTableHeader>
                                <DataTableHeader>{translate("Ship-To")}</DataTableHeader>
                                <DataTableHeader>{translate("Status")}</DataTableHeader>
                                {/* Xng-Changes: adding PO# */}
                                {fields.showPONumber && (
                                    <DataTableHeader>{translate("PO #")}</DataTableHeader>
                                )}
                                <DataTableHeader>{translate("Total")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {ordersDataView.value.map(
                                    order =>
                                        order !== null && (
                                            <DataTableRow
                                                className="product-row"
                                                data-test-selector="recentOrders_dataLine"
                                                key={order.id}
                                            >
                                                <OrderSummaryCard
                                                    showPO={fields.showPONumber}
                                                    order={order}
                                                    orderSettings={settingsCollection.orderSettings}
                                                    extendedStyles={styles.orderSummaryCard}
                                                />
                                            </DataTableRow>
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </GridItem>
                )}
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 8,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            // XNG-Changes
            {
                name: fields.title,
                displayName: "Widget Title",
                editorTemplate: "TextField",
                defaultValue: "Recent Orders",
                fieldType: "Translatable",
                sortOrder: 2,
            },
            {
                name: fields.sectionTitleUrl,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Translatable",
                sortOrder: 3,
                isRequired: true,
            },
            {
                name: fields.sectionTitleText,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "View all Orders",
                fieldType: "Translatable",
                sortOrder: 4,
            },
            {
                name: fields.showPONumber,
                displayName: "Show PO #",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
